import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  faArrowsRotate,
  faCheck,
  faDownload,
  faEye,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
} from 'antd';
import { styled } from 'styled-components';
import { useLocations } from '@/api/Locations';
import { vessel } from '@/api/Vessel/Vessel';
import {
  useClosePortCall,
  useCompleteReadiness,
  useDeleteFerryingPeriod,
  usePortCalls,
  useSaveFerryingPeriod,
  useStartDischarge,
  useVessels,
} from '@/api/Vessel';
import { useTranslate } from '@/translations';
import { VesselPortCall } from '@/types';
import { CustomColumns, CustomTable, TooltipIcon } from '@/ui/components';
import { timeConverter } from '@/utils/helpers';
import { AddJourneyModal } from './AddJourneyModal';
import { EditPortCallModal } from './EditPortCallModal';
import { AddManifestModal } from './ManifestModal';
import { PortCallDetail } from './PortCallDetail';

const STATUS = ['Upcoming', 'InPort', 'Discharging', 'Completed'] as const;

const statusLabel = (status?: string) =>
  status === 'InPort' ? 'In Port' : status || '—';

const statusColor = (status?: string) => {
  if (status === 'Upcoming') return 'blue';
  if (status === 'InPort') return 'gold';
  if (status === 'Discharging') return 'green';
  if (status === 'Completed') return 'default';
  return 'default';
};

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const saveBlob = (data: Blob | undefined, fileName: string) => {
  if (!data) return;
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const errorText = (value: unknown, fallback: string) => {
  if (typeof value === 'string' && value.trim()) return value;
  if (value && typeof value === 'object' && 'title' in value) {
    const title = (value as { title?: unknown }).title;
    if (typeof title === 'string' && title.trim()) return title;
  }
  return fallback;
};

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

type FerryingValues = {
  name?: string;
  plannedStart: dayjs.Dayjs;
  plannedEnd: dayjs.Dayjs;
  plannedUnits: number;
  actualUnits?: number;
  teamOrLane?: string;
};

export const PortCallsTab = ({
  canOperate,
  onAddManifest,
}: {
  canOperate: boolean;
  onAddManifest?: (vesselName?: string) => void;
}) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [status, setStatus] = useState<(typeof STATUS)[number]>('Upcoming');
  const [siteId, setSiteId] = useState<number>();
  const [vesselId, setVesselId] = useState<number>();
  const [shippingReference, setShippingReference] = useState('');
  const [expandedId, setExpandedId] = useState<number>();
  const [addOpen, setAddOpen] = useState(false);
  const [addManifestOpen, setAddManifestOpen] = useState(false);
  const [editing, setEditing] = useState<VesselPortCall>();
  const [periodFor, setPeriodFor] = useState<VesselPortCall>();
  const [downloadingId, setDownloadingId] = useState<number>();
  const [statusRowId, setStatusRowId] = useState<number>();
  const [periodForm] = Form.useForm<FerryingValues>();
  const locations = useLocations();
  const vessels = useVessels();
  const portCalls = usePortCalls(undefined, true, {
    siteId,
    vesselId,
    shippingReference: shippingReference || undefined,
  });
  const savePeriod = useSaveFerryingPeriod();
  const deletePeriod = useDeleteFerryingPeriod();
  const completeCheck = useCompleteReadiness();
  const startDischarge = useStartDischarge();
  const closePortCall = useClosePortCall();
  const locationsLoading = locations.isPending || locations.isFetching;
  const vesselsLoading = vessels.isPending || vessels.isFetching;
  const allRows = portCalls.data ?? [];
  const rows = allRows.filter((row) => row.status === status);
  const statusCounts = STATUS.reduce(
    (counts, item) => {
      counts[item] = allRows.filter((row) => row.status === item).length;
      return counts;
    },
    {} as Record<(typeof STATUS)[number], number>,
  );
  const siteOptions = (locations.data ?? []).map((location) => ({
    value: location.id,
    label: location.desc || location.code,
  }));
  const vesselOptions = (vessels.data ?? []).map((vessel) => ({
    value: vessel.id,
    label: vessel.vesselName,
  }));

  const saveFerrying = async (values: FerryingValues) => {
    if (!periodFor) return;
    const result = await savePeriod.mutateAsync({
      id: periodFor.id,
      body: {
        name: values.name,
        plannedStart: values.plannedStart.toISOString(),
        plannedEnd: values.plannedEnd.toISOString(),
        plannedUnits: values.plannedUnits,
        actualUnits: values.actualUnits,
        teamOrLane: values.teamOrLane,
      },
    });
    if (!result.success) {
      message.error(result.message || t('generalError'));
      return;
    }
    setPeriodFor(undefined);
    periodForm.resetFields();
  };

  const autoCreatePlan = async (row: VesselPortCall) => {
    const quantity = row.plannedVehicleQty ?? 0;
    if (!row.eta || quantity <= 0) {
      message.error(t('autoCreatePlanNeedsQty'));
      return;
    }
    const start = dayjs(row.eta);
    const end = row.etd ? dayjs(row.etd) : start.add(8, 'hour');
    if (!end.isAfter(start)) {
      message.error(t('autoCreatePlanNeedsQty'));
      return;
    }
    const result = await savePeriod.mutateAsync({
      id: row.id,
      body: {
        name: 'P1',
        plannedStart: start.toISOString(),
        plannedEnd: end.toISOString(),
        plannedUnits: quantity,
      },
    });
    if (!result.success) message.error(result.message || t('generalError'));
  };

  const downloadManifest = async (row: VesselPortCall) => {
    if (!row.manifestId) {
      message.warning(t('noPortCallManifest'));
      return;
    }
    setDownloadingId(row.id);
    try {
      const response = await vessel.downloadShippingManifestByType(
        row.manifestId,
        'manifest',
      );
      if (!response.success || !response.data) {
        message.error(errorText(response.message, t('generalError')));
        return;
      }
      saveBlob(response.data, `${row.manifestRef || 'manifest'}.pdf`);
    } finally {
      setDownloadingId(undefined);
    }
  };

  const advanceStatus = async (row: VesselPortCall) => {
    setStatusRowId(row.id);
    try {
      const result =
        row.status === 'InPort'
          ? await startDischarge.mutateAsync(row.id)
          : await closePortCall.mutateAsync(row.id);
      if (!result.success) {
        message.error(errorText(result.message, t('generalError')));
      }
    } finally {
      setStatusRowId(undefined);
    }
  };

  const columns: CustomColumns<VesselPortCall> = [
    {
      title: t('portCall'),
      dataIndex: 'portCallRef',
      width: 130,
      fixed: 'left',
    },
    {
      title: t('vessel'),
      dataIndex: 'imo',
      width: 130,
      render: (_, record) => `${record.vesselName} (${record.imo})`,
    },
    { title: t('siteTerminal'), dataIndex: 'site', width: 180 },
    {
      dataIndex: 'eta',
      title: status === 'Upcoming' ? t('etaUtc') : t('etaAta'),
      width: status === 'Upcoming' ? 180 : 360,
      render: (_: unknown, row: VesselPortCall) =>
        status === 'Upcoming'
          ? timeConverter(row.eta)
          : `${timeConverter(row.eta)} ~ ${timeConverter(row.ata)}`,
    },
    {
      title: t('manifestRef'),
      dataIndex: 'manifestRef',
      width: 130,
      render: (value?: string) => value || t('notCreated'),
    },
    {
      dataIndex: 'plannedVehicleQty',
      title: status === 'Completed' ? t('vehiclesPlan') : t('vehicles'),
      width: 100,
      render: (_: unknown, row: VesselPortCall) =>
        row.plannedVehicleQty ?? (row.ferryingPlannedUnits || '—'),
    },
    ...(status === 'Upcoming'
      ? ([
          {
            title: t('ferryingPlan'),
            dataIndex: 'ferryingPlan',
            render: (value?: string) => (value ? <Tag>{value}</Tag> : '-'),
          },
        ] satisfies CustomColumns<VesselPortCall>)
      : ([
          {
            title:
              status === 'Completed' ? t('vehiclesActual') : t('discharged'),
            dataIndex: 'discharged',
            width: 110,
            align: 'center',
            render: (value: number, row: VesselPortCall) =>
              status === 'Discharging' || status === 'Completed'
                ? `${value} / ${row.plannedVehicleQty ?? (row.ferryingPlannedUnits || 0)}`
                : value,
          },
        ] satisfies CustomColumns<VesselPortCall>)),
    ...(status === 'Completed'
      ? ([
          {
            title: t('variance'),
            dataIndex: 'discharged',
            width: 100,
            align: 'center',
            render: (_: unknown, row: VesselPortCall) => {
              const plan = row.plannedVehicleQty ?? row.ferryingPlannedUnits;
              if (!plan) return '—';
              return `${row.discharged - plan}`;
            },
          },
          {
            title: t('completedOn'),
            dataIndex: 'completedOn',
            width: 180,
            render: (value?: string) => timeConverter(value),
          },
        ] satisfies CustomColumns<VesselPortCall>)
      : []),
    {
      title: t('status'),
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value?: string) => (
        <Tag color={statusColor(value)}>{statusLabel(value)}</Tag>
      ),
    },
    {
      dataIndex: 'id',
      title: t('actions'),
      width: 168,
      align: 'center',
      fixed: 'right',
      render: (_: unknown, row: VesselPortCall) => (
        <Space>
          {canOperate && row.status !== 'Completed' ? (
            <TooltipIcon
              title={t('editPortCall')}
              icon={faPenToSquare}
              onClick={() => setEditing(row)}
            />
          ) : null}
          <TooltipIcon
            title={t('viewPortCallDetails')}
            icon={faEye}
            onClick={() =>
              setExpandedId((current) =>
                current === row.id ? undefined : row.id,
              )
            }
          />
          {canOperate && row.status === 'Discharging' ? (
            statusRowId === row.id ? (
              <Spin size="small" />
            ) : (
              <Popconfirm
                title={t('closePortCall')}
                onConfirm={() => void advanceStatus(row)}
              >
                <TooltipIcon title={t('closePortCall')} icon={faCheck} />
              </Popconfirm>
            )
          ) : null}
          {downloadingId === row.id ? (
            <Spin size="small" />
          ) : (
            <TooltipIcon
              title={t('download')}
              icon={faDownload}
              onClick={() => void downloadManifest(row)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Toolbar>
        <StatusBar>
          <Segmented
            value={status}
            onChange={(value) => {
              setStatus(value as (typeof STATUS)[number]);
              setExpandedId(undefined);
            }}
            options={STATUS.map((item) => ({
              label: `${statusLabel(item)} (${statusCounts[item]})`,
              value: item,
            }))}
          />
          <TooltipIcon
            title={t('dashboard.refresh')}
            icon={faArrowsRotate}
            spin={portCalls.isFetching}
            onClick={() => void portCalls.refetch()}
          />
        </StatusBar>
        {canOperate ? (
          <Space>
            <Button type="primary" onClick={() => setAddOpen(true)}>
              {t('addJourneyPortCall')}
            </Button>
            <Button onClick={() => setAddManifestOpen(true)}>
              {t('addManifest')}
            </Button>
          </Space>
        ) : null}
      </Toolbar>
      <Filters>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('siteTerminal')}
          style={{ width: 180 }}
          options={siteOptions}
          value={siteId}
          loading={locationsLoading}
          notFoundContent={locationsLoading ? <Spin size="small" /> : undefined}
          onChange={setSiteId}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('vessel')}
          style={{ width: 180 }}
          options={vesselOptions}
          value={vesselId}
          loading={vesselsLoading}
          notFoundContent={vesselsLoading ? <Spin size="small" /> : undefined}
          onChange={setVesselId}
        />
        <Input
          allowClear
          placeholder={t('shippingReference')}
          style={{ width: 180 }}
          value={shippingReference}
          onChange={(event) => setShippingReference(event.target.value)}
        />
        <Select
          allowClear
          placeholder={t('status')}
          style={{ width: 180 }}
          options={STATUS.map((item) => ({
            label: statusLabel(item),
            value: item,
          }))}
          value={vesselId}
          onChange={setVesselId}
        />
      </Filters>
      <CustomTable
        rowKey="id"
        loading={portCalls.isFetching}
        dataSource={rows}
        pagination={{ pageSize: 8 }}
        columns={columns}
        expandable={{
          expandedRowKeys: expandedId == null ? [] : [expandedId],
          onExpand: (expanded, row) =>
            setExpandedId(expanded ? row.id : undefined),
          expandedRowRender: (row) => (
            <PortCallDetail
              row={row}
              canOperate={canOperate && row.status !== 'Completed'}
              onAddPeriod={() => setPeriodFor(row)}
              onAutoCreate={() => void autoCreatePlan(row)}
              onOpenManifest={onAddManifest}
              onDeletePeriod={(period) =>
                deletePeriod.mutateAsync({
                  id: row.id,
                  periodId: period.id,
                })
              }
              onToggleCheck={(checkId, complete) =>
                completeCheck.mutateAsync({
                  id: row.id,
                  checkId,
                  complete,
                })
              }
              onStart={() => startDischarge.mutateAsync(row.id)}
              onClose={() => closePortCall.mutateAsync(row.id)}
              pending={{
                savePeriod: savePeriod.isPending,
                deletePeriod: deletePeriod.isPending,
                start: startDischarge.isPending,
                close: closePortCall.isPending,
                check: completeCheck.isPending,
              }}
            />
          ),
        }}
        scroll={{ x: 1400 }}
      />
      <AddJourneyModal open={addOpen} onClose={() => setAddOpen(false)} />
      {addManifestOpen ? (
        <AddManifestModal open onClose={() => setAddManifestOpen(false)} />
      ) : null}
      <EditPortCallModal
        portCall={editing}
        onClose={() => setEditing(undefined)}
      />
      <Modal
        title={t('addFerryingPeriod')}
        open={Boolean(periodFor)}
        onCancel={() => setPeriodFor(undefined)}
        onOk={() => periodForm.submit()}
        confirmLoading={savePeriod.isPending}
        destroyOnHidden
      >
        <Form
          form={periodForm}
          layout="vertical"
          onFinish={(values) => void saveFerrying(values)}
        >
          <Form.Item name="name" label={t('period')}>
            <Input placeholder="P1" />
          </Form.Item>
          <Form.Item
            name="plannedStart"
            label={t('plannedStart')}
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item
            name="plannedEnd"
            label={t('plannedEnd')}
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item
            name="plannedUnits"
            label={t('units')}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="teamOrLane" label={t('teamOrLane')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
